import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Base styles: LDS flat — solid fills, no glow/scale, radius-lg (8px).
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium',
    'transition-colors duration-200 ease-out',
    'disabled:pointer-events-none disabled:opacity-50',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
    'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
  ].join(' '),
  {
    variants: {
      variant: {
        default: ['bg-primary text-primary-foreground', 'hover:bg-primary/90'].join(' '),
        destructive: [
          'bg-destructive text-white',
          'hover:bg-destructive/90',
          'focus-visible:ring-destructive/30 dark:focus-visible:ring-destructive/40',
          'dark:bg-destructive/80',
        ].join(' '),
        outline: [
          'border border-border bg-background',
          'hover:bg-accent hover:text-accent-foreground',
        ].join(' '),
        secondary: ['bg-secondary text-secondary-foreground', 'hover:bg-secondary/80'].join(' '),
        ghost: ['hover:bg-accent hover:text-accent-foreground'].join(' '),
        link: ['text-primary underline-offset-4', 'hover:underline hover:text-primary/80'].join(
          ' '
        ),
      },
      size: {
        default: 'h-10 px-5 py-2 has-[>svg]:px-4',
        sm: 'h-8 rounded-md gap-1.5 px-3 text-xs has-[>svg]:px-2.5',
        lg: 'h-12 rounded-lg px-8 text-base has-[>svg]:px-6',
        icon: 'size-10 rounded-lg',
        'icon-sm': 'size-8 rounded-md',
        'icon-lg': 'size-12 rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
